import { useState, useEffect, useRef } from "react";
import { apiUrl, executeRequest, getToken } from "../../functions/requests";
import Spacer from "../../components/Spacer";
import FormInput from "../../components/FormInput";
import Button from "../../components/Button";
import SearchBar from "../../components/SearchBar";
import IconCheck from "../../icons/IconCheck";
import IconFolder from "../../icons/IconFolder";
import IconFile from "../../icons/IconFile";
import Message from "../../components/Message";

export default function KnowledgeBaseRecordUpdate({ record, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchingRecords, setSearchingRecords] = useState(false);
  const [searchRecordsResults, setSearchRecordsResults] = useState([]);
  const [linkedRecordsDetails, setLinkedRecordsDetails] = useState(
    record.linked_records
  );
  const [recordToUpdateData, setRecordToUpdateData] = useState(record);
  const [message, setMessage] = useState("");
  const [messageSection, setMessageSection] = useState(null);

  const closeMessage = () => {
    setMessage("");
    setMessageSection(null);
  };

  const debounceTimeout = useRef(null); // Ref to hold timeout

  // Send request to update record
  const updateRecord = async () => {
    setLoading(true);
    console.log(recordToUpdateData);
    await executeRequest(
      apiUrl + "knowledge-base-records/" + recordToUpdateData.uuid,
      "POST",
      JSON.stringify(recordToUpdateData),
      {
        Authorization: "Bearer " + getToken(),
        "Content-type": "application/json",
      },
      (data) => {
        console.log(data);
        onUpdated(data);
        setMessage("Record successfully updated.");
        setMessageSection("success");
      },
      (error) => {
        console.log(error);
        setMessage("Error updating record.");
        setMessageSection("warning");
      }
    );
    setLoading(false);
  };

  // Debounced search function
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchRecordsResults([]); // Reset results if query is empty
      return;
    }

    setSearchingRecords(true);

    // Clear the previous timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set a new timeout for 500 ms
    debounceTimeout.current = setTimeout(async () => {
      await executeRequest(
        apiUrl + "knowledge-base-records/search",
        "POST",
        JSON.stringify({
          query: searchQuery,
          linked_records: recordToUpdateData.linked_records,
        }),
        {
          Authorization: "Bearer " + getToken(),
          "Content-type": "application/json",
        },
        (data) => {
          console.log(data);
          setSearchRecordsResults(data);
        },
        (error) => {
          console.error(error);
        }
      );
      setSearchingRecords(false);
    }, 500);

    // Cleanup timeout on unmount or query change
    return () => clearTimeout(debounceTimeout.current);
  }, [searchQuery]);

  // Function to handle user typing
  const handleSearchInput = (query) => {
    setSearchQuery(query);
  };

  // Function to reset search input
  const handleSearchReset = () => {
    setSearchQuery("");
    setSearchRecordsResults([]);
    setSearchingRecords(false);
  };

  // Function to toggle linked records (add or remove uuid)
  const toggleLinkedRecord = (uuid, name, description, path) => {
    // console.log(recordToUpdateData.linked_records);
    // Update the linkedRecordsDetails with full record details
    setLinkedRecordsDetails((prev) => {
      console.log(description);

      // Check if the uuid is already in the array
      const existingRecordIndex = prev.findIndex(
        (record) => record.uuid === uuid
      );

      console.log("EXISTING RECORD INDEX: " + existingRecordIndex);

      let updatedLinkedRecordsDetails = [...prev];

      if (existingRecordIndex !== -1) {
        // Remove the uuid if it exists
        updatedLinkedRecordsDetails.splice(existingRecordIndex, 1);
      } else {
        // Add the uuid if it doesn't exist
        updatedLinkedRecordsDetails.push({
          uuid: uuid,
          name: name,
          description: description,
          path: path,
        });
      }

      console.log("UPDATED RECORDS DETAILS");
      console.log(updatedLinkedRecordsDetails);

      setLinkedRecordsDetails(updatedLinkedRecordsDetails);
    });

    setRecordToUpdateData((prev) => {
      const updatedLinkedRecords = prev.linked_records.includes(uuid)
        ? prev.linked_records.filter((id) => id !== uuid) // Remove if exists
        : [...prev.linked_records, uuid]; // Add if not exists

      return {
        ...prev,
        linked_records: updatedLinkedRecords,
      };
    });
  };

  return (
    <>
      <h1 className="page-title">Update {record.name}</h1>
      <Spacer />
      <Spacer />
      <div className="flex flex-gap-m">
        <div style={{ width: "70%" }}>
          <FormInput
            title="Name *"
            type="text"
            value={recordToUpdateData.name}
            onChanged={(value) =>
              setRecordToUpdateData((prev) => ({
                ...prev,
                name: value,
              }))
            }
          />
          <Spacer />
          <Spacer />
          <FormInput
            title="Description"
            type="textarea"
            value={recordToUpdateData.description}
            onChanged={(value) =>
              setRecordToUpdateData((prev) => ({
                ...prev,
                description: value,
              }))
            }
          />
          {record.type === "file" && (
            <>
              <Spacer />
              <FormInput
                title="Content"
                type="rich_editor"
                value={recordToUpdateData.content}
                language={recordToUpdateData.content_language || "markdown"}
                onLanguageChanged={(value) =>
                  setRecordToUpdateData((prev) => ({
                    ...prev,
                    content_language: value,
                  }))
                }
                onChanged={(value) =>
                  setRecordToUpdateData((prev) => ({
                    ...prev,
                    content: value,
                  }))
                }
              />
            </>
          )}
        </div>
        <div style={{ width: "30%" }}>
          <h2>Linked records:</h2>
          <Spacer height="var(--spaceS)" />
          <SearchBar
            placeholder="Search records..."
            onUserTyped={handleSearchInput}
            onUserInputReset={handleSearchReset}
            isDisabled={loading}
          />
          <Spacer />
          <div>Selected:</div>
          <Spacer height="var(--spaceS)" />
          {!linkedRecordsDetails ||
            (linkedRecordsDetails.length < 1 && <div>No records selected</div>)}
          {linkedRecordsDetails && linkedRecordsDetails.length > 0 && (
            <>
              {linkedRecordsDetails.map((linkedRecord, index) => {
                return (
                  <>
                    <div
                      key={index}
                      title="Remove"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        loading
                          ? null
                          : toggleLinkedRecord(
                              linkedRecord.uuid,
                              linkedRecord.name,
                              linkedRecord.description,
                              linkedRecord.path
                            )
                      }
                    >
                      <div className="flex flex-gap-s flex-align-center">
                        {linkedRecord.type === "dir" ? (
                          <IconFolder />
                        ) : (
                          <IconFile />
                        )}{" "}
                        {linkedRecord.name}
                      </div>
                      <Spacer height="var(--spaceXS)" />
                      <div>{linkedRecord.description}</div>
                      <Spacer height="var(--spaceXS)" />
                      <div>{linkedRecord.path}</div>
                    </div>
                    <Spacer height="var(--spaceS)" />
                  </>
                );
              })}
            </>
          )}
          <Spacer />
          {searchRecordsResults.length > 0 ? (
            <>
              <div>Results:</div>
              <Spacer height="var(--spaceS)" />
              {searchRecordsResults.map((searchResult, index) => {
                if (
                  linkedRecordsDetails.filter(
                    (record) => record.uuid === searchResult.uuid
                  ).length > 0
                ) {
                  return;
                }

                return (
                  <>
                    <div
                      key={index}
                      title="Add"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        loading
                          ? null
                          : toggleLinkedRecord(
                              searchResult.uuid,
                              searchResult.name,
                              searchResult.description,
                              searchResult.path
                            )
                      }
                    >
                      <div className="flex flex-gap-s flex-align-center">
                        {searchResult.type === "dir" ? (
                          <IconFolder />
                        ) : (
                          <IconFile />
                        )}{" "}
                        {searchResult.name}
                      </div>
                      <Spacer height="var(--spaceXS)" />
                      <div>{searchResult.description}</div>
                      <Spacer height="var(--spaceXS)" />
                      <div>{searchResult.path}</div>
                    </div>
                    <Spacer height="var(--spaceS)" />
                  </>
                );
              })}
            </>
          ) : (
            searchQuery !== "" &&
            !searchingRecords && (
              <>
                <div>No records found.</div>
              </>
            )
          )}
          <Spacer />
          <Button
            text="Update"
            icon={<IconCheck />}
            loading={loading}
            disabled={recordToUpdateData.name.length < 1}
            onClicked={() =>
              recordToUpdateData.name.length > 0 ? updateRecord() : null
            }
          />
        </div>
      </div>
      <Spacer />
      {message.length > 0 && messageSection !== null && (
        <Message
          message={message}
          type={messageSection}
          onClosed={() => closeMessage()}
        />
      )}
    </>
  );
}
